/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createRef, useState } from 'react';
import { IonContent, IonHeader, IonPage, IonLabel, IonGrid, IonRow, IonCol, IonSlides, IonSlide, IonButton, IonIcon, IonText } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { ComputeBMI, GetHeightValues, GetWeightValues } from '../utils/BMIUtils';
import { useUnitSystem } from '../utils/Settings';
import Toolbar from '../components/Toolbar';
import { isValid } from '../utils/CVRiskUtils';
import { arrowBackOutline } from 'ionicons/icons';
import './CVRisk.scss';
import './BodyMassIndex.scss';
import HeightInput from '../components/HeightInput';
import NumericInput from '../components/NumericInput';
import { useGA4React } from 'ga-4-react';

const BodyMassIndex: React.FC = (props) => {

  const [t] = useTranslation();
  const ga = useGA4React();
  const [height, setHeight] = useState<any>(175);
  const [weight, setWeight] = useState<any>(80);
  const [weightIf, setWeightIf] = useState<any>(80);
  const [weightInitial, setWeightInitial] = useState<any>(80);

  const [unitSystem] = useUnitSystem();
  let sliderRef = createRef<HTMLIonSlidesElement>();

  const heightValues = GetHeightValues(unitSystem);
  const weightValues = GetWeightValues(unitSystem);
  let isValidWeightIf = isValid(weightIf, weightValues[0], weightValues[1]);
  let isValidWeightInitial = isValid(weightInitial, weightValues[0], weightValues[1]);
  let isValidHeight = isValid(height, heightValues[0], heightValues[1]);

  const bmi: number = ComputeBMI(weight, height, unitSystem);
  const bmiLevel = [{
    level: 35,
    desc: 'Obesity Class 2'
  }, {
    level: 30,
    desc: 'Obesity Class 1'
  }, {
    level: 25,
    desc: 'Pre-obesity'
  }, {
    level: 18.5,
    desc: 'Normal Weight'
  }, {
    level: 0,
    desc: 'Underweight'
  }];

  if (ga)
    ga.event('calculateBMI', 'calculate', bmi.toString());

  function onCalculate() {
    setWeight(weightInitial);
    setWeightIf(weightInitial);
    sliderRef.current?.slideNext();
  }

  function onReCalculate() {
    setWeight(weightIf);
  }
  function goBackForm() {
    sliderRef.current?.slidePrev();
  }

  let sliderOptions = {
    followFinger: false,
    allowTouchMove: false
  };

  return (
    <IonPage id="bmi-page">
      <IonHeader>
        <Toolbar title={t('Body Mass Index')} />
      </IonHeader>
      <IonContent fullscreen>
        <IonSlides className="calculatorSlides" ref={sliderRef} pager={true} scrollbar={true} options={sliderOptions}>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Body Mass Index')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="12">
                  &nbsp;
                </IonCol>
                <IonCol size-sm="3">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Height')}</b><br />({heightValues[2]}):</IonLabel>
                    </IonCol>
                    <IonCol>
                      <HeightInput height={height} setHeight={(height: number) => setHeight(height)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size-sm="3" offset-sm="1">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Weight')}</b><br />({weightValues[2]})</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={weightInitial} min={weightValues[0]} max={weightValues[1]} onValueChange={(value: number) => setWeightInitial(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="4">
                  <IonButton className="calculateButton" disabled={!(isValidHeight && isValidWeightInitial)} fill="solid" onClick={onCalculate}>{t('Calculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Body Mass Index')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size-sm="12">
                  <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                    <IonIcon icon={arrowBackOutline} slot="icon-only" />
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="6">
                  <IonLabel className="riskValue">{t('Body Mass Index')}: {bmi.toFixed(1)}</IonLabel>
                  <IonGrid className="bmiRiskGrid">
                    {
                      bmiLevel.map((risklevel: any, index: number) => {
                        return <IonRow key={index}>
                          <IonCol size="4" className={`bmiRisk${4 - index}`}>&nbsp;
                            {
                              bmi >= risklevel.level && (index === 0 || bmi < bmiLevel[index - 1].level) && <IonLabel ></IonLabel>
                            }
                          </IonCol>
                          <IonCol size="8">&nbsp;
                            {
                              bmi >= risklevel.level && (index === 0 || bmi < bmiLevel[index - 1].level) && <IonLabel>&lt;&nbsp;{t(risklevel.desc)}</IonLabel>
                            }
                          </IonCol>
                        </IonRow>
                      })
                    }
                  </IonGrid>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size-sm="12">
                  <IonLabel><b>{t('What Would Happen If')}</b></IonLabel>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size-sm="12">
                  <IonLabel><b>{t('Weight')}</b><br />({weightValues[2]})</IonLabel>
                </IonCol>
                <IonCol size-sm="12">
                  <NumericInput value={weightIf} min={weightValues[0]} max={weightValues[1]} onValueChange={(value: number) => setWeightIf(value)} />
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size-sm="4">
                  <IonButton className="calculateButton" disabled={!isValidWeightIf} fill="solid" onClick={onReCalculate}>{t('Recalculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
        </IonSlides>
      </IonContent>
    </IonPage>
  );
};

export default BodyMassIndex;
